import React, { useMemo } from 'react';

import AddLinkIcon from '@mui/icons-material/AddLink';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Link, List, ListItem, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Bedbank } from '@luxuryescapes/contract-svc-bedbank';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { getSlug } from '~/utils/slugUtils';

import { CoreQuery, SupplierManageAction } from './type';

interface Props {
  count: number;
  data: Array<Bedbank.ListSupplierProperty>;
  queries: CoreQuery;
  openModal: (id: string, action: SupplierManageAction, masterPropertyId?: string) => void;
  onPageChange: (page: number, limit: number) => void;
  onSortChange: (orderBy: CoreQuery['orderBy'], sortBy: CoreQuery['sortBy']) => void;
}

export default function PropertiesTable({ count, data, queries, onPageChange, onSortChange, openModal }: Props) {
  const columns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'supplierPropertyId',
        headerName: 'Id',
        disableColumnMenu: true,
        flex: 1,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'supplier',
        headerName: 'Supplier',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'hotelName',
        headerName: 'Name',
        disableColumnMenu: true,
        flex: 2,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'masterProperties',
        headerName: 'Master Properties',
        disableColumnMenu: true,
        flex: 5,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <Box>
            <List>
              {row.masterProperties.map((masterProperty) => (
                <ListItem key={masterProperty.id}>
                  <Typography
                    color={
                      masterProperty.state === 'suggestion'
                        ? 'info'
                        : masterProperty.state === 'deleted'
                        ? 'warning'
                        : 'success'
                    }
                  >
                    <Link href={`/bedbank/properties/${masterProperty.id}`} underline="hover" color="inherit">
                      {masterProperty.name}
                    </Link>{' '}
                    ({masterProperty.id}) ({masterProperty.supplier})
                  </Typography>
                  <Tooltip title="Preview on site">
                    <IconButton
                      size="small"
                      href={`${window.configs['LUX_CUSTOMER_PORTAL']}/partner/${getSlug(masterProperty.name)}/${
                        masterProperty.id
                      }?preview=true`}
                      target="_blank"
                      rel="noopener"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {row.allowContentActions.includes('detach') &&
                    ['mapped', 'deleted'].includes(masterProperty.state) && (
                      <Tooltip title="Detach">
                        <IconButton onClick={() => openModal(row.id, 'detach', masterProperty.id)}>
                          <LinkOffIcon fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    )}
                  {row.allowContentActions.includes('attach') && masterProperty.state === 'suggestion' && (
                    <Tooltip title="Attach">
                      <IconButton onClick={() => openModal(row.id, 'attach', masterProperty.id)}>
                        <AddLinkIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        ),
      },
      {
        field: 'countryCode',
        headerName: 'Address',
        disableColumnMenu: true,
        display: 'flex',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <div>
            <div>
              Country Code: <b>{row.countryCode}</b>
            </div>
            <div>{row.address}</div>
            <div>Latitude: {row.latitude}</div>
            <div>Longitude: {row.longitude}</div>
          </div>
        ),
        flex: 2,
      },
      {
        field: 'rating',
        headerName: 'Star Rating',
        disableColumnMenu: true,
        flex: 1,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'numberOfRooms',
        headerName: 'Number Of Rooms',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'numberOfImages',
        headerName: 'Number Of Images',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'allowContentActions',
        headerName: 'Actions',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <Box>
            {!queries.filterByMapping && row.allowContentActions.includes('attach') && (
              <Tooltip title="Attach">
                <IconButton onClick={() => openModal(row.id, 'attach')}>
                  <AddLinkIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
            {row.masterProperties.filter((x) => x.state === 'mapped').length === 0 &&
              row.allowContentActions.includes('master') && (
                <Tooltip title="De Couple">
                  <IconButton onClick={() => openModal(row.id, 'master')}>
                    <InsertLinkIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            {!queries.filterByMapping && (
              <Tooltip title="Mapping">
                <IconButton
                  href={`/bedbank/properties?page=1&limit=100&orderBy=asc&sortBy=hotelName&filterById=${row.id}&filterByMapping=true#suppliers_properties`}
                  target="_blank"
                >
                  <AutoFixHigh fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [openModal, queries],
  );

  return (
    <>
      <DataGridPro
        columns={columns}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
        rows={data ?? []}
        pagination
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={{ page: queries.page - 1, pageSize: queries.limit }}
        sortModel={[{ field: queries.sortBy, sort: queries.orderBy }]}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page + 1, pageSize)}
        onSortModelChange={(s) =>
          onSortChange(s[0]?.sort ?? 'asc', (s[0]?.field ?? queries.sortBy) as CoreQuery['sortBy'])
        }
        paginationMode="server"
        sortingMode="server"
        rowCount={count}
        density="compact"
        slots={{ pagination: GridPagination }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
      />
    </>
  );
}
